import {useAuth} from '@apps-common/auth-react';
import {
  BASE_LAYERS,
  BASE_LAYERS_STYLES,
} from '@apps-common/ui-maps/constants.js';
import Map from '@planet/maps/Map';
import View from '@planet/maps/View';
import MapboxLayer from '@planet/maps/layer/MapboxVector';
import TileLayer from '@planet/maps/layer/WebGLTile';
import XYZSource from '@planet/maps/source/XYZ';

import {always} from 'ol/events/condition';
import DragZoom from 'ol/interaction/DragZoom';
import {defaults as defaultInteractions} from 'ol/interaction/defaults';
import {array, func, object} from 'prop-types';
import React, {useCallback, useEffect, useMemo, useRef} from 'react';
import {Provider, useDispatch, useSelector} from 'react-redux';

import CollectionLayer from './CollectionLayer.jsx';
import EditableVectorLayer from './EditableVectorLayer.jsx';
import {deselectFeatureRef, selectFeatureRef} from '@actions/selected-features';
import {useGetCollection, useGetInfiniteCollections} from '@api/collections';
import {fitExtent, setDrawTool} from '@app/actions/map';
import {API_TARGET, MAPBOX_ACCESS_TOKEN} from '@app/config';
import {MAP_DRAW_LAYERS, MAP_TOOLS, MAX_ZOOM} from '@app/constants';
import useCollectionParams from '@app/hooks/useCollectionParams';
import useMapViewParams from '@app/hooks/useMapViewParams';
import useOrgParams from '@app/hooks/useOrgParams';
import store from '@app/store';
import {LEFT_DRAWER_WIDTH, RIGHT_DRAWER_WIDTH} from '@app/styles/dimensions.js';
import {getHashFromId, refEquals} from '@app/util/granero';

const dragZoomInteraction = new DragZoom({condition: always});

const ViewerMap = ({
  mapProps,
  measureFeatures,
  setMeasureFeatures,
  setTempGeometry,
}) => {
  const {user} = useAuth();
  const dispatch = useDispatch();
  const {center, zoom, setLocation} = useMapViewParams();
  const {orgId} = useOrgParams();
  const {collectionId} = useCollectionParams();

  const viewRef = useRef(null);
  const mapRef = useRef(null);

  //state props
  const boxZoomEnabled = useSelector(
    state => state.map.drawTool.name === MAP_TOOLS.BOX_ZOOM
  );
  const drawTool = useSelector(state => state.map.drawTool);
  const isLeftDrawerOpen = useSelector(state => state.ui.drawers.left !== '');
  const isRightDrawerOpen = useSelector(state => state.ui.drawers.right !== '');
  const mapNextExtent = useSelector(state => state.map.fitExtent);
  const overlays = useSelector(state => state.map.overlays);
  const baseLayer = useSelector(state => state.map.baseLayer);
  const visibleCollectionIds = useSelector(
    state => state.collections.visibleIds
  );
  const selectedFeatureRef = useSelector(state => state.selectedFeatureRef);

  const {data: collectionsData} = useGetInfiniteCollections(
    {orgId},
    {enabled: !collectionId}
  );

  const {data: collection} = useGetCollection(
    {
      collectionId: collectionId,
    },
    {
      enabled: !!collectionId,
    }
  );

  const visibleCollections = useMemo(() => {
    if (visibleCollectionIds.length) {
      const allCollections = collectionsData?.collections;

      return visibleCollectionIds.map(vc => {
        const collectionFromAll = allCollections?.find(
          ac => getHashFromId(ac.id) === vc
        );

        if (collectionFromAll) {
          return collectionFromAll;
        } else if (collectionId && collection) {
          return getHashFromId(collection.id) === vc ? collection : null;
        } else {
          return null;
        }
      });
    }

    return [];
  }, [
    collection,
    collectionId,
    collectionsData?.collections,
    visibleCollectionIds,
  ]);

  const onViewChange = useCallback(
    event => {
      const view = event.target;
      const newCenter = view.getCenter();
      const newZoom = view.getZoom();

      setLocation(newCenter, newZoom);
    },
    [setLocation]
  );

  const zoomToExtent = useCallback(
    extent => {
      const buffer = 16;
      const padding = [
        buffer,
        (isRightDrawerOpen ? RIGHT_DRAWER_WIDTH : 0) + buffer,
        buffer,
        (isLeftDrawerOpen ? LEFT_DRAWER_WIDTH : 0) + buffer,
      ];

      return new Promise(resolve => {
        // 100ms gives the layer a chance to redraw
        //  OL will not redraw while zooming.
        setTimeout(() => {
          viewRef.current.fit(extent, {
            padding,
            duration: 800,
          });
        }, 100);

        resolve();
      });
    },
    [isLeftDrawerOpen, isRightDrawerOpen]
  );

  useEffect(() => {
    if (mapNextExtent.length === 4) {
      zoomToExtent(mapNextExtent).then(() => {
        dispatch(fitExtent([]));
      });
    }
  }, [mapNextExtent, zoomToExtent, dispatch]);

  const onAddMeasureFeature = useCallback(
    newFeature => {
      setMeasureFeatures(measureFeatures.concat([newFeature]));
    },
    [measureFeatures, setMeasureFeatures]
  );

  useEffect(() => {
    const resetBoxZoom = () => {
      dispatch(
        setDrawTool({
          name: MAP_TOOLS.NONE,
          layer: MAP_DRAW_LAYERS.NONE,
          target: '',
        })
      );
    };
    dragZoomInteraction.on('boxend', resetBoxZoom);

    return () => {
      dragZoomInteraction.un('boxend', resetBoxZoom);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    dragZoomInteraction.setActive(boxZoomEnabled);
  }, [boxZoomEnabled]);

  useEffect(() => {
    if (mapRef.current) {
      let cursor = 'default';
      if (drawTool.name !== MAP_TOOLS.NONE) {
        cursor = 'crosshair';
      }
      mapRef.current.getTarget().style.cursor = cursor;
    }
  }, [drawTool]);

  return (
    <React.Fragment>
      <Map
        controls={[]}
        {...mapProps}
        ref={node => {
          mapRef.current = node;
          mapProps.ref(node);
        }}
        interactions={defaultInteractions().extend([dragZoomInteraction])}
      >
        <Provider store={store}>
          <View
            ref={viewRef}
            center={center}
            zoom={zoom}
            options={{
              maxZoom: MAX_ZOOM,
            }}
            onChange={onViewChange}
          />

          <MapboxLayer
            visible={baseLayer === BASE_LAYERS.DARK}
            options={{
              accessToken: MAPBOX_ACCESS_TOKEN,
              styleUrl: BASE_LAYERS_STYLES[BASE_LAYERS.DARK].background,
            }}
            onSourceReady={event => {
              event.target
                .getSource()
                .setAttributions([
                  '©<a href="https://www.mapbox.com/about/maps/" target="_blank">Mapbox</a>',
                  '©<a href="http://www.openstreetmap.org/copyright" target="_blank">OpenStreetMap</a>',
                ]);
            }}
          />

          <MapboxLayer
            visible={baseLayer === BASE_LAYERS.LIGHT}
            options={{
              accessToken: MAPBOX_ACCESS_TOKEN,
              styleUrl: BASE_LAYERS_STYLES[BASE_LAYERS.LIGHT].background,
            }}
            onSourceReady={event => {
              event.target
                .getSource()
                .setAttributions([
                  '©<a href="https://www.mapbox.com/about/maps/" target="_blank">Mapbox</a>',
                  '©<a href="http://www.openstreetmap.org/copyright" target="_blank">OpenStreetMap</a>',
                ]);
            }}
          />

          <TileLayer visible={baseLayer === BASE_LAYERS.SATELLITE}>
            <XYZSource
              url={`${BASE_LAYERS_STYLES[BASE_LAYERS.SATELLITE].background(API_TARGET, user.accessToken)}`}
              options={{
                crossOrigin: 'anonymous',
                attributions: ['©Planet Labs PBC'],
              }}
            />
          </TileLayer>

          <EditableVectorLayer
            layerName={MAP_DRAW_LAYERS.MEASURE}
            features={measureFeatures}
            drawTool={drawTool}
            onAddFeature={onAddMeasureFeature}
            setTempGeometry={setTempGeometry}
          />

          <MapboxLayer
            visible={overlays.labels}
            key={`labels_${baseLayer}`}
            options={{
              accessToken: MAPBOX_ACCESS_TOKEN,
              styleUrl: BASE_LAYERS_STYLES[baseLayer]?.labels,
            }}
            onSourceReady={event => {
              event.target
                .getSource()
                .setAttributions([
                  '©<a href="https://www.mapbox.com/about/maps/" target="_blank">Mapbox</a>',
                  '©<a href="http://www.openstreetmap.org/copyright" target="_blank">OpenStreetMap</a>',
                ]);
            }}
          />

          {visibleCollections.filter(Boolean).map(collection => (
            <CollectionLayer
              key={`${collection.id}_${collection.feature_count}`}
              collectionId={collection.id}
              extent={
                collection.extent?.spatial?.bbox?.length > 0
                  ? collection.extent.spatial.bbox[0]
                  : null
              }
              token={user?.accessToken}
              selectedFeatures={[selectedFeatureRef]}
              onClick={(event, features) => {
                if (features.length > 0) {
                  if (
                    selectedFeatureRef &&
                    refEquals(
                      selectedFeatureRef,
                      features[0].properties_['pl:ref']
                    )
                  ) {
                    dispatch(deselectFeatureRef());
                  } else {
                    dispatch(
                      selectFeatureRef(features[0].properties_['pl:ref'])
                    );
                  }
                }
              }}
            />
          ))}
        </Provider>
      </Map>
    </React.Fragment>
  );
};

ViewerMap.propTypes = {
  mapProps: object,
  measureFeatures: array,
  searchFeatures: array,
  setMeasureFeatures: func,
  setSearchFeatures: func,
  setTempGeometry: func,
};

export default ViewerMap;
